import { createDepositWorker } from "./createWorker";
import manifest from "./manifest.json";
import { privateTokenPool } from "../joinsplit-v2/privatePools";
import {
  assertDepositProvingDomain,
  type DepositProvingDomain,
} from "./deployment";

export type DepositWitness = DepositProvingDomain & {
  npk: string;
  tokenHash: string;
  amount: string;
  recoveryDigest?: string;
};
export interface LocalDepositProof {
  success: boolean;
  commitment: string;
  token_hash: string;
  amount: string;
  recovery_digest?: string;
  proof: unknown;
  proving_time_ms: number;
}
export const DEPOSIT_PROVE_TIMEOUT_MS = 180_000;
let busy = false;

export function assertDepositProvingDeployment(
  chainId: number,
  pool: string,
): void {
  const recovery = [
    manifest.recovery.deployments.base,
    manifest.recovery.deployments.doge,
  ].find((pin) => pin.chainId === chainId);
  if (
    recovery?.pools.some(
      (address) => address.toLowerCase() === pool.toLowerCase(),
    )
  )
    return;
  if (chainId === manifest.dogeosChikyu.chainId) {
    assertDepositProvingDomain({ chainId });
    if (pool.toLowerCase() === manifest.dogeosChikyu.pool.toLowerCase()) return;
    throw new Error(
      "Development deposit keys are restricted to the pinned DogeOS Chikyu deployment",
    );
  }
  if (
    chainId !== manifest.chainId ||
    (pool.toLowerCase() !== manifest.pool.toLowerCase() &&
      pool.toLowerCase() !== privateTokenPool()?.toLowerCase())
  ) {
    throw new Error(
      "Development deposit keys are restricted to the pinned Base Sepolia deployment",
    );
  }
}

/** Fail closed on timeout, cancellation, unsupported browsers or bad assets. No remote fallback. */
export async function proveDepositLocally(
  witness: DepositWitness,
  signal?: AbortSignal,
  onProgress?: (stage: "loading" | "proving") => void,
): Promise<LocalDepositProof> {
  assertDepositProvingDomain(witness);
  if (
    witness.recoveryDigest !== undefined &&
    (!/^0x[0-9a-fA-F]{64}$/.test(witness.recoveryDigest) ||
      BigInt(witness.recoveryDigest) === 0n ||
      BigInt(witness.recoveryDigest) >=
        21888242871839275222246405745257275088548364400416034343698204186575808495617n)
  )
    throw new Error("Invalid deposit recovery digest");
  if (signal?.aborted)
    throw new DOMException("Deposit proving cancelled", "AbortError");
  if (busy) throw new Error("Deposit proving is already running");
  if (
    typeof Worker === "undefined" ||
    typeof WebAssembly === "undefined" ||
    !globalThis.crypto?.subtle
  ) {
    throw new Error(
      "This browser does not support local WASM proving in a secure context",
    );
  }
  busy = true;
  let worker: Worker | undefined;
  try {
    worker = createDepositWorker();
    const activeWorker = worker;
    return await new Promise<LocalDepositProof>((resolve, reject) => {
      const finish = (error?: Error, result?: LocalDepositProof) => {
        clearTimeout(timer);
        signal?.removeEventListener("abort", abort);
        activeWorker.onmessage = null;
        activeWorker.onerror = null;
        activeWorker.onmessageerror = null;
        if (error) reject(error);
        else resolve(result!);
      };
      const abort = () =>
        finish(new DOMException("Deposit proving cancelled", "AbortError"));
      const timer = setTimeout(
        () => finish(new Error("Local deposit proving timed out")),
        DEPOSIT_PROVE_TIMEOUT_MS,
      );
      signal?.addEventListener("abort", abort, { once: true });
      activeWorker.onerror = () =>
        finish(new Error("Local deposit proving worker failed"));
      activeWorker.onmessageerror = () =>
        finish(new Error("Invalid local deposit proving response"));
      activeWorker.onmessage = ({ data }) => {
        if (data?.type === "progress") {
          if (data.stage === "loading" || data.stage === "proving") {
            try {
              onProgress?.(data.stage);
            } catch {
              // Presentation must not interrupt proving or its cleanup.
            }
          }
          return;
        }
        if (data?.type === "result") {
          if (
            witness.recoveryDigest !== undefined &&
            data.result?.recovery_digest?.toLowerCase() !==
              witness.recoveryDigest.toLowerCase()
          )
            finish(new Error("Deposit proof recovery digest mismatch"));
          else finish(undefined, data.result);
        } else finish(new Error("Local deposit proving failed"));
      };
      if (signal?.aborted) {
        abort();
        return;
      }
      try {
        // Explicit fields prevent callers from accidentally copying wallet keys.
        activeWorker.postMessage({
          type: "prove",
          ...(witness.solana
            ? {
                solana: {
                  cluster: witness.solana.cluster,
                  programId: witness.solana.programId,
                  allowDevelopmentSetup: witness.solana.allowDevelopmentSetup,
                },
              }
            : { chainId: witness.chainId }),
          npk: witness.npk,
          tokenHash: witness.tokenHash,
          amount: witness.amount,
          ...(witness.recoveryDigest !== undefined
            ? { recoveryDigest: witness.recoveryDigest }
            : {}),
        });
      } catch {
        finish(new Error("Could not start local deposit proving"));
      }
    });
  } finally {
    worker?.terminate();
    busy = false;
  }
}
